/**
 * Menu / catalogue product card (web).
 *
 * ONE component for what OrderStoreDetail previously hand-rolled five times
 * (section-filter grid, section-filter list, section grid, section list, and
 * search results). Those five copies had drifted apart — most visibly, the
 * search-result card ignored item-level discounts entirely, so a discounted
 * item found via search displayed AND added at full price.
 *
 * Three layouts, mirroring the app (mobile/app/order-store.tsx):
 *   card — takeaway product card. Image, name, description, price, add
 *          control. This is the appetising treatment; food needs it.
 *   tile — two-up (and wider) grid tile. Image-led.
 *   row  — dense catalogue row for grocery SKUs. Hairline separators inside
 *          one sheet, so five hundred SKUs do not become five hundred cards.
 *
 * Which one to use is the caller's decision, driven by `deriveCatalogueMode`
 * in ./store-utils. This file contains no availability, cart or pricing
 * policy — only `effectivePriceOf`, which is deliberately identical to the
 * server's own computation.
 */
import { Minus, Package, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { ELEVATION, FOCUS_RING, RADII, TYPE } from "./tokens";
import { pence } from "./store-utils";

export type MenuItemLike = {
  id: number;
  name: string;
  ageRestricted?: boolean;
  minimumAge?: number;
  description?: string | null;
  pricePence: number;
  imageUrl?: string | null;
  sectionId: number;
  available?: boolean | null;
  discountType?: string | null;
  discountValue?: number | null;
  discountLabel?: string | null;
  calories?: number | null;
  multiBuyQty?: number | null;
  multiBuyPricePence?: number | null;
  isEndToEnd?: boolean;
};

/**
 * Single-unit price after any item-level discount.
 *
 * Deliberately the SAME arithmetic the server uses when it recomputes the
 * basket (api-server/src/routes/order.ts:544) so the price a customer is
 * shown is the price they are charged:
 *
 *     percentage → Math.round(raw * (100 - value) / 100)
 *     fixed      → Math.max(0, raw - value)
 *
 * The previous inline version used `raw * (1 - value/100)`, which is a
 * different floating-point expression, and omitted the `Math.max(0, …)`
 * clamp — so a fixed discount larger than the price produced a negative
 * line price on the client that the server would never agree with.
 *
 * Integer pence throughout (Platform LAW 5).
 */
export function effectivePriceOf(item: Pick<MenuItemLike, "pricePence" | "discountType" | "discountValue">): number {
  const { pricePence: raw, discountType, discountValue } = item;
  if (discountType === "percentage" && discountValue != null && discountValue > 0) {
    return Math.round((raw * (100 - discountValue)) / 100);
  }
  if (discountType === "fixed" && discountValue != null && discountValue > 0) {
    return Math.max(0, raw - discountValue);
  }
  return raw;
}

/** True when the item carries a discount worth showing a struck-through price for. */
function hasDiscount(item: MenuItemLike): boolean {
  return effectivePriceOf(item) !== item.pricePence;
}

function discountLabelOf(item: MenuItemLike): string | null {
  if (item.discountLabel) return item.discountLabel;
  if (!hasDiscount(item)) return null;
  if (item.discountType === "percentage") return `${item.discountValue}% off`;
  return `${pence(item.discountValue ?? 0)} off`;
}

export type MixMatchDeal = { id: number; qty: number; pricePence: number; itemIds: number[]; active: boolean };

export type MenuItemCardProps = {
  item: MenuItemLike;
  layout: "card" | "tile" | "row";
  /** Quantity of this menu item currently in the basket. */
  qty: number;
  /** True when the item has option groups — add always opens the picker. */
  hasOptions: boolean;
  /** Store closed / checkout blocked. Add controls are disabled, not hidden. */
  disabled?: boolean;
  mixMatch?: MixMatchDeal[];
  /** Called with the effective (discounted) unit price. */
  onAdd: (effectivePricePence: number) => void;
  onIncrement: () => void;
  onDecrement: () => void;
  /** Grocery catalogues drop per-item descriptions to stay scannable. */
  dense?: boolean;
  /** Show retail pack details without clamping grocery product names. */
  showFullName?: boolean;
  /** Hairline top border — `row` layout inside a continuous sheet. */
  showTopRule?: boolean;
  className?: string;
};

/**
 * Quantity stepper.
 *
 * The visual button stays compact so it does not dominate a tile, but a
 * pseudo-element expands the hit area to the 44px minimum on touch. That is
 * how the control can be both dense and thumb-friendly.
 */
function QtyControls({
  qty, hasOptions, disabled, compact, name, onAdd, onIncrement, onDecrement,
}: {
  qty: number; hasOptions: boolean; disabled?: boolean; compact?: boolean; name: string;
  onAdd: () => void; onIncrement: () => void; onDecrement: () => void;
}) {
  const size = compact ? "w-7 h-7" : "w-8 h-8";
  const hit = "relative before:absolute before:content-[''] before:-inset-2 sm:before:inset-0";
  const isStepper = qty > 0 && !hasOptions;

  return (
    <div className="flex items-center gap-1.5 shrink-0">
      {isStepper && (
        <>
          <button
            type="button"
            onClick={onDecrement}
            disabled={disabled}
            aria-label={qty === 1 ? `Remove ${name} from basket` : `Decrease quantity of ${name}`}
            className={cn(
              size, hit, FOCUS_RING,
              "grid place-items-center rounded-full border border-slate-200 text-slate-600",
              "hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors",
            )}
          >
            <Minus size={compact ? 11 : 13} />
          </button>
          <span className={cn(TYPE.bodyStrong, "min-w-[18px] text-center tabular-nums text-slate-900")}>{qty}</span>
        </>
      )}
      {qty > 0 && hasOptions && (
        <span className={cn(TYPE.footnote, "font-semibold text-[#1e3a5f] whitespace-nowrap")}>{qty} in basket</span>
      )}
      <button
        type="button"
        onClick={isStepper ? onIncrement : onAdd}
        disabled={disabled}
        aria-label={isStepper ? `Increase quantity of ${name}` : `Add ${name} to basket`}
        className={cn(
          size, hit, FOCUS_RING,
          "grid place-items-center rounded-full bg-[#1e3a5f] text-white",
          "hover:bg-[#1e3a5f]/90 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors",
        )}
      >
        <Plus size={compact ? 11 : 13} />
      </button>
    </div>
  );
}

/** Price plus the quiet metadata that belongs beside it — never badges. */
function PriceCluster({ item, soldOut, mixMatch, hasOptions }: {
  item: MenuItemLike; soldOut: boolean; mixMatch: MixMatchDeal[]; hasOptions: boolean;
}) {
  const effective = effectivePriceOf(item);
  const discounted = hasDiscount(item);
  const deals = mixMatch.filter((d) => d.active && d.itemIds.includes(item.id));

  return (
    <div className="min-w-0">
      <div className="flex items-baseline gap-1.5 flex-wrap">
        <span className={cn(TYPE.bodyStrong, soldOut ? "text-slate-400" : "text-slate-900")}>
          {pence(effective)}
        </span>
        {discounted && (
          <span className={cn(TYPE.footnote, "text-slate-400 line-through")}>{pence(item.pricePence)}</span>
        )}
      </div>
      {soldOut ? (
        <span className={cn(TYPE.footnote, "font-semibold text-red-600")}>Sold out</span>
      ) : (
        <div className="flex flex-wrap items-center gap-x-2">
          {item.multiBuyQty != null && item.multiBuyPricePence != null && (
            <span className={cn(TYPE.footnote, "font-semibold text-amber-600")}>
              {item.multiBuyQty} for {pence(item.multiBuyPricePence)}
            </span>
          )}
          {deals.map((d) => (
            <span key={d.id} className={cn(TYPE.footnote, "font-semibold text-teal-600")}>
              Any {d.qty} for {pence(d.pricePence)}
            </span>
          ))}
          {hasOptions && <span className={cn(TYPE.footnote, "text-slate-500")}>Choose options</span>}
        </div>
      )}
    </div>
  );
}

/** Image with the badges that genuinely help scanning a grid. */
function ItemImage({
  item, className, rounded, qty, showBadges,
}: { item: MenuItemLike; className: string; rounded: string; qty: number; showBadges?: boolean }) {
  const label = discountLabelOf(item);
  return (
    <div className={cn("relative overflow-hidden bg-slate-100 shrink-0", className, rounded)}>
      {item.imageUrl ? (
        <img src={item.imageUrl} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full grid place-items-center"><Package size={24} className="text-slate-300" /></div>
      )}
      {showBadges && label && (
        <span className={cn(TYPE.overline, RADII.xs, "absolute top-1.5 left-1.5 bg-[#fff8e6] text-[#1e3a5f] px-1.5 py-0.5")}>
          {label}
        </span>
      )}
      {showBadges && qty > 0 && (
        <span className={cn(
          TYPE.caption, "absolute top-1.5 right-1.5 grid place-items-center w-5 h-5 rounded-full bg-[#1e3a5f] text-white shadow",
        )}>
          {qty}
        </span>
      )}
    </div>
  );
}

export function MenuItemCard({
  item, layout, qty, hasOptions, disabled, mixMatch = [],
  onAdd, onIncrement, onDecrement, dense, showFullName = false, showTopRule, className,
}: MenuItemCardProps) {
  const soldOut = item.available === false;
  const effective = effectivePriceOf(item);
  const add = () => onAdd(effective);
  const label = discountLabelOf(item);
  const illustrativeImage = item.imageUrl?.includes("woodstock-illustrative-") === true;
  const packPriceLabel = showFullName && /\bPack of \d+\b/i.test(item.name) ? "Price for the whole pack" : null;

  const controls = (compact: boolean) => (
    <QtyControls
      qty={qty} hasOptions={hasOptions} disabled={disabled || soldOut} compact={compact}
      name={item.name} onAdd={add} onIncrement={onIncrement} onDecrement={onDecrement}
    />
  );

  /* ── tile — image-led grid cell ─────────────────────────────────────
     Grocery catalogues drop the card silhouette (`dense`) so a wall of
     SKUs reads as a grid of products rather than a wall of boxes. */
  if (layout === "tile") {
    return (
      <article className={cn(
        "flex flex-col",
        !dense && cn(RADII.md, "bg-white border border-slate-200/80 overflow-hidden"),
        !item.imageUrl && "self-start w-full",
        soldOut && "opacity-60",
        className,
      )}>
        {item.imageUrl && <ItemImage
          item={item}
          qty={qty}
          showBadges
          className="w-full aspect-square"
          rounded={dense ? RADII.sm : ""}
        />}
        <div className={cn("flex flex-col flex-1 gap-1", dense ? "pt-2" : "p-2.5")}>
          {!item.imageUrl && label && <span className="self-start rounded-md bg-[#fff8e6] px-2 py-1 text-xs font-semibold text-[#1e3a5f]">{label}</span>}
          <h3 className={cn(TYPE.subhead, "font-semibold text-slate-900", showFullName ? "break-words" : "line-clamp-2")}>{item.name}</h3>
          {item.ageRestricted && <span className="inline-flex self-start rounded-md bg-red-50 px-2 py-1 text-xs font-bold text-red-800" title="Original photo ID and date of birth required at delivery. Challenge 25. No unattended delivery.">{item.minimumAge ?? 18}+ · Photo ID required</span>}
          {illustrativeImage && <p className={cn(TYPE.footnote, "text-slate-500")}>Illustrative image</p>}
          {packPriceLabel && <p className={cn(TYPE.footnote, "text-slate-500")}>{packPriceLabel}</p>}
          {!dense && item.description && (
            <p className={cn(TYPE.footnote, "text-slate-500 whitespace-pre-line")}>{item.description}</p>
          )}
          {item.calories != null && (
            <p className={cn(TYPE.footnote, "text-slate-400")}>{item.calories} kcal</p>
          )}
          <div className="flex items-end justify-between gap-2 mt-auto pt-1.5">
            <PriceCluster item={item} soldOut={soldOut} mixMatch={mixMatch} hasOptions={hasOptions} />
            {!soldOut && controls(true)}
          </div>
        </div>
      </article>
    );
  }

  /* ── row — dense catalogue row inside one continuous sheet ────────── */
  if (layout === "row") {
    return (
      <article className={cn(
        "flex gap-3 items-start px-3 py-2.5",
        showTopRule && "border-t border-slate-100",
        !item.imageUrl && "self-start w-full",
        soldOut && "opacity-60",
        className,
      )}>
        <div className="flex-1 min-w-0">
          <h3 className={cn("text-[21px] font-semibold leading-7 tracking-tight text-[#1e3a5f]", showFullName ? "break-words" : "line-clamp-2")}>{item.name}</h3>
          {item.ageRestricted && <span className="inline-flex self-start rounded-md bg-red-50 px-2 py-1 text-xs font-bold text-red-800" title="Original photo ID and date of birth required at delivery. Challenge 25. No unattended delivery.">{item.minimumAge ?? 18}+ · Photo ID required</span>}
          {illustrativeImage && <p className={cn(TYPE.footnote, "text-slate-500")}>Illustrative image</p>}
          {packPriceLabel && <p className={cn(TYPE.footnote, "text-slate-500")}>{packPriceLabel}</p>}
          {item.description && (
            <p className={cn(TYPE.footnote, "text-slate-500 whitespace-pre-line mt-0.5")}>{item.description}</p>
          )}
          <div className="flex items-end justify-between gap-3 mt-1.5">
            <PriceCluster item={item} soldOut={soldOut} mixMatch={mixMatch} hasOptions={hasOptions} />
            {!soldOut && controls(false)}
          </div>
        </div>
        {item.imageUrl && (
          <ItemImage item={item} qty={qty} className="w-[80px] h-[80px]" rounded={RADII.sm} />
        )}
      </article>
    );
  }

  /* ── card — the takeaway product card ───────────────────────────────
     Items with no image simply have no image column, so the text expands
     into the full width rather than sitting beside an empty box. */
  return (
    <article className={cn(
      RADII.md,
      "flex flex-col gap-4 bg-white border-b border-slate-100 pb-6 overflow-hidden transition-colors",
      !item.imageUrl && "self-start w-full",
      soldOut && "opacity-60",
      className,
    )}>
      {item.imageUrl && <ItemImage item={item} qty={qty} className="w-full aspect-[1.6]" rounded="rounded-[20px]" />}
      <div className="flex-1 min-w-0 px-1">
        {/* Name on its own line; badges wrap below so a long "Collect & Return"
            label can never crowd or collide with the image column. */}
        <h3 className={cn("text-[21px] font-semibold leading-7 tracking-tight text-[#1e3a5f]", showFullName ? "break-words" : "line-clamp-2")}>{item.name}</h3>
          {item.ageRestricted && <span className="inline-flex self-start rounded-md bg-red-50 px-2 py-1 text-xs font-bold text-red-800" title="Original photo ID and date of birth required at delivery. Challenge 25. No unattended delivery.">{item.minimumAge ?? 18}+ · Photo ID required</span>}
          {illustrativeImage && <p className={cn(TYPE.footnote, "text-slate-500")}>Illustrative image</p>}
          {packPriceLabel && <p className={cn(TYPE.footnote, "text-slate-500")}>{packPriceLabel}</p>}
        {(item.isEndToEnd || label) && (
          <div className="flex flex-wrap items-center gap-1 mt-1">
            {item.isEndToEnd && (
              <span className={cn(TYPE.overline, RADII.xs, "bg-[#1e3a5f] text-white px-1.5 py-0.5")}>
                Collect &amp; Return
              </span>
            )}
            {label && (
              <span className={cn(TYPE.overline, RADII.xs, "bg-[#fff8e6] text-[#1e3a5f] px-1.5 py-0.5")}>{label}</span>
            )}
          </div>
        )}
        {item.description && (
          <p className={cn(TYPE.footnote, "text-slate-500 whitespace-pre-line mt-1 leading-[17px]")}>{item.description}</p>
        )}
        {item.calories != null && (
          <p className={cn(TYPE.footnote, "text-slate-400 mt-0.5")}>{item.calories} kcal</p>
        )}
        <div className="flex items-end justify-between gap-3 mt-2">
          <PriceCluster item={item} soldOut={soldOut} mixMatch={mixMatch} hasOptions={hasOptions} />
          {!soldOut && controls(false)}
        </div>
      </div>

    </article>
  );
}
